/**
 * Read-only summary of an employee's advances, shown inside EmployeeDetail (admin view).
 * Lists every advance (newest first) with type, amount, status, signature state.
 *
 * Props:
 *  - employeeId (string, required)
 *  - onStatsChange?: ({count, total, toSign}) => void   — notifies parent for the side widget
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtEur } from "../lib/format";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import {
    Wallet,
    Loader2,
    CheckCircle2,
    AlertCircle,
    ExternalLink,
    Printer,
} from "lucide-react";
import { AdvancePdfPreviewDialog } from "./AdvancePdfPreviewDialog";

const TYPE_LABELS = {
    ACCONTO: "Acconto",
    FNG: "F.N.G.",
    TFR: "T.F.R.",
    PRESTITO: "Prestito",
    ANF: "A.N.F.",
    EMOLUMENTI: "Emolumenti",
    ACCONTO_FERIE_NON_GODUTE: "Acconto ferie non godute",
    ACCONTO_TFR: "Acconto T.F.R.",
};

const STATUS_BADGES = {
    issued: { label: "Da firmare", color: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
    signed: { label: "Firmato", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    recovered: { label: "Recuperato", color: "bg-slate-100 text-slate-600 border-slate-300", icon: CheckCircle2 },
    cancelled: { label: "Annullato", color: "bg-red-50 text-red-700 border-red-200", icon: AlertCircle },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
};

export const AdminEmployeeAdvancesPanel = forwardRef(function AdminEmployeeAdvancesPanel(
    { employeeId, onStatsChange },
    ref,
) {
    const [advances, setAdvances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfAdvance, setPdfAdvance] = useState(null);

    const fetchData = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/advances?employee_id=${employeeId}`);
            setAdvances(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Errore caricamento acconti:", e);
            setAdvances([]);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useImperativeHandle(ref, () => ({ refresh: fetchData }), [fetchData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Notify parent of stats for the side widget
    useEffect(() => {
        if (loading || !onStatsChange) return;
        const total = advances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
        const toSign = advances.filter(
            (a) => (a.status === "issued" || a.status === "recovered") && !a.signed_at,
        ).length;
        onStatsChange({ count: advances.length, total, toSign });
    }, [advances, loading, onStatsChange]);

    if (loading) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-emp-advances-panel">
                <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </section>
        );
    }

    const total = advances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const toSign = advances.filter(
        (a) => (a.status === "issued" || a.status === "recovered") && !a.signed_at,
    ).length;

    return (
        <section
            className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
            data-testid="admin-emp-advances-panel"
        >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                        <Wallet className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-bold text-[#376DB3]">
                            Acconti del dipendente
                        </h3>
                        <p className="text-xs text-slate-500">
                            {advances.length === 0
                                ? "Nessun acconto registrato"
                                : `${advances.length} ricevut${advances.length === 1 ? "a" : "e"} · Totale ${fmtEur(total)}${toSign > 0 ? ` · ${toSign} da firmare` : ""}`}
                        </p>
                    </div>
                </div>
                {advances.length > 0 && (
                    <Link to={`/acconti?employee_id=${employeeId}`}>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            data-testid="admin-emp-advances-view-all"
                        >
                            Gestisci in Acconti
                            <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                    </Link>
                )}
            </div>

            {advances.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    Questo dipendente non ha ancora ricevuto acconti, F.N.G., T.F.R. o prestiti.
                    <div className="mt-2 text-xs">
                        Per registrare un nuovo acconto, vai alla pagina{" "}
                        <Link to="/acconti" className="font-semibold text-[#376DB3] hover:underline">
                            Acconti
                        </Link>{" "}
                        e clicca su &quot;Nuovo acconto&quot;.
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="font-semibold">N° Ricevuta</TableHead>
                                <TableHead className="font-semibold">Data</TableHead>
                                <TableHead className="font-semibold">Tipo</TableHead>
                                <TableHead className="text-right font-semibold">Importo</TableHead>
                                <TableHead className="font-semibold">Stato</TableHead>
                                <TableHead className="font-semibold">Firma</TableHead>
                                <TableHead className="text-right font-semibold">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {advances.map((a) => {
                                const statusInfo = STATUS_BADGES[a.status] || STATUS_BADGES.issued;
                                const StatusIcon = statusInfo.icon;
                                return (
                                    <TableRow key={a.id} data-testid={`admin-advance-row-${a.id}`}>
                                        <TableCell className="font-mono-numbers font-medium">
                                            {a.receipt_number || "—"}
                                        </TableCell>
                                        <TableCell className="font-mono-numbers text-slate-600">
                                            {fmtDate(a.date)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="font-mono-numbers text-xs">
                                                {TYPE_LABELS[a.type] || a.type}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono-numbers font-semibold text-[#376DB3]">
                                            {fmtEur(a.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`gap-1 ${statusInfo.color}`}>
                                                <StatusIcon className="h-3 w-3" />
                                                {statusInfo.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-slate-600">
                                            {a.signed_at ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    {fmtDate(a.signed_at)}
                                                </span>
                                            ) : (
                                                <span className="text-amber-600">In attesa</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Link to={`/acconti/${a.id}`}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 gap-1 px-2 text-xs text-[#376DB3] hover:bg-[#376DB3]/10"
                                                        data-testid={`admin-advance-detail-${a.id}`}
                                                    >
                                                        <ExternalLink className="h-3 w-3" />
                                                        Dettaglio
                                                    </Button>
                                                </Link>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 gap-1 px-2 text-xs"
                                                    onClick={() => setPdfAdvance(a)}
                                                    data-testid={`admin-advance-receipt-${a.id}`}
                                                >
                                                    <Printer className="h-3 w-3" />
                                                    Ricevuta
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
            <AdvancePdfPreviewDialog
                advance={pdfAdvance}
                open={!!pdfAdvance}
                onClose={() => setPdfAdvance(null)}
            />
        </section>
    );
});
